// src/pages/JobDetails.jsx
import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    FiArrowLeft,
    FiMapPin,
    FiClock,
    FiBriefcase,
    FiCalendar,
    FiDollarSign,
    FiCheckCircle,
    FiUpload,
    FiLoader,
} from "react-icons/fi";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Input,
    Label,
    FormGroup,
    FormFeedback,
    Modal,
    ModalHeader,
    ModalBody,
} from "reactstrap";
import { LuBuilding2 } from "react-icons/lu";
import { toast } from "react-toastify";

import "./JobDetails.css";
import { getAllJobs, submitApplication } from "../../services/api";

const JobDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [job, setJob] = useState(null);
    // console.log(job);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [applyModal, setApplyModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [form, setForm] = useState({
        fullName: "",
        email: "",
        phone: "",
        currentLocation: "",
        totalExperience: "",
        skills: "",
        coverLetter: "",
        resume: null,
    });

    const [errors, setErrors] = useState({});

    const ALLOWED_FORMATS = [".pdf", ".doc", ".docx"];
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

    // Fetch job details
    useEffect(() => {
        const fetchJob = async () => {
            try {
                setLoading(true);
                const data = await getAllJobs();
                const foundJob = data.find((j) => j.id === id);

                if (foundJob && foundJob.status === "Published") {
                    setJob({
                        id: foundJob.id,
                        title: foundJob.title,
                        department: foundJob.department,
                        location: foundJob.location?.trim(),
                        type: foundJob.employmentType,
                        experience: foundJob.experienceRequired?.trim(),
                        salary: foundJob.salaryRange,
                        description: foundJob.description,
                        skills: foundJob.requiredSkills
                            ? foundJob.requiredSkills.split(",").map(s => s.trim()).filter(Boolean)
                            : [],
                        deadline: foundJob.applicationDeadline,
                        responsibilities: foundJob.keyResponsibilities,
                    });
                } else {
                    setError("Job not found or no longer available");
                }
            } catch (err) {
                setError("Failed to load job details");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchJob();
    }, [id]);

    const validateForm = () => {
        const newErrors = {};
        if (!form.fullName.trim()) newErrors.fullName = "Full name is required";
        if (!form.email.trim()) newErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = "Invalid email format";
        if (!form.phone.trim()) newErrors.phone = "Phone number is required";
        if (!form.currentLocation.trim()) newErrors.currentLocation = "Location is required";
        if (!form.totalExperience.trim()) newErrors.totalExperience = "Experience is required";
        if (!form.skills.trim()) newErrors.skills = "Skills are required";
        if (!form.resume) newErrors.resume = "Resume is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const ext = "." + file.name.split(".").pop()?.toLowerCase();
        if (!ALLOWED_FORMATS.includes(ext)) {
            setErrors((prev) => ({ ...prev, resume: "Only PDF, DOC, DOCX allowed" }));
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            setErrors((prev) => ({ ...prev, resume: "File size must be under 5MB" }));
            return;
        }

        setForm((prev) => ({ ...prev, resume: file }));
        setErrors((prev) => {
            const { resume, ...rest } = prev;
            return rest;
        });
    };

    // Real API submission (using FormData)
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm() || !job) return;

        setSubmitting(true);

        try {
            const formData = new FormData();
            formData.append("jobId", job.id);
            formData.append("fullName", form.fullName);
            formData.append("email", form.email);
            formData.append("phone", form.phone);
            formData.append("currentLocation", form.currentLocation);
            formData.append("totalExperience", form.totalExperience);
            formData.append("skills", form.skills);
            formData.append("coverLetter", form.coverLetter || "");

            if (form.resume) {
                formData.append("resume", form.resume);
            }

            // Call the API function from api.js
            await submitApplication(formData);

            setSubmitted(true);
            toast.success("Application submitted successfully!", {
                position: "top-right",
                autoClose: 4000,
            });
        } catch (err) {
            toast.error(err.message || "Failed to submit application. Please try again.");
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => {
                const { [field]: _, ...rest } = prev;
                return rest;
            });
        }
    };

    // Default responsibilities (you can make this dynamic later if needed)
    // const responsibilities = [
    //     "Collaborate with cross-functional teams to deliver high-quality solutions",
    //     "Participate in code reviews and contribute to best practices",
    //     "Mentor junior team members and contribute to knowledge sharing",
    //     "Work with stakeholders to understand requirements and translate into technical solutions",
    //     "Continuously improve processes and tools",
    // ];

    if (loading) {
        return <div className="min-vh-100 d-flex align-items-center justify-content-center">Loading job details...</div>;
    }

    if (error || !job) {
        return (
            <div className="min-vh-100 d-flex flex-column">
                <div className="flex-grow-1 d-flex align-items-center justify-content-center py-5">
                    <div className="text-center">
                        <h2 className="mb-3 fw-bold">Job Not Found</h2>
                        <p className="text-muted mb-4">{error || "This position is no longer available."}</p>
                        <Button color="primary" onClick={() => navigate("/careers")}>
                            Back to Careers
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="job-details-page min-vh-100 bg-light">
            <div className="">
                {/* Hero / Header Section - unchanged */}
                <section className="job-details-hero bg-gradient-primary text-white position-relative py-5 py-md-6">
                    <Container>
                        <Link
                            to="/careers"
                            className="d-inline-flex align-items-center gap-2 text-white-75 hover-text-white mb-4 text-decoration-none back-job-btn"
                        >
                            <FiArrowLeft size={18} /> Back to all jobs
                        </Link>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <Row className="align-items-end">
                                <Col lg={8}>
                                    <div className="d-flex align-items-center gap-3 mb-3">
                                        <div className="rounded-circle bg-white-25 d-flex align-items-center justify-content-center" style={{ width: 56, height: 56 }}>
                                            <LuBuilding2 size={24} />
                                        </div>
                                        <span color="light" pill className="px-4 py-2 subtitle-badge">
                                            {job.department}
                                        </span>
                                    </div>

                                    <h1 className="job-details-title fw-bold mb-3">{job.title}</h1>

                                    <div className="d-flex flex-wrap gap-4 text-white-75 small">
                                        <span className="d-flex align-items-center gap-2">
                                            <FiMapPin size={16} /> {job.location}
                                        </span>
                                        <span className="d-flex align-items-center gap-2">
                                            <FiBriefcase size={16} /> {job.type}
                                        </span>
                                        <span className="d-flex align-items-center gap-2">
                                            <FiClock size={16} /> {job.experience}
                                        </span>
                                        {job.salary && (
                                            <span className="d-flex align-items-center gap-2">
                                                <FiDollarSign size={16} /> {job.salary}
                                            </span>
                                        )}
                                        <span className="d-flex align-items-center gap-2">
                                            <FiCalendar size={16} /> Deadline: {job.deadline}
                                        </span>
                                    </div>
                                </Col>

                                <Col lg={4} className="mt-4 mt-lg-0 text-lg-end">
                                    <Button
                                        color="light"
                                        className="px-5 fw-semibold apply-btn"
                                        onClick={() => setApplyModal(true)}
                                    >
                                        Apply Now
                                    </Button>
                                </Col>
                            </Row>
                        </motion.div>
                    </Container>
                </section>

                {/* Rest of your JSX remains exactly the same */}
                <Container className="py-5">
                    <Row>
                        <Col lg={8}>
                            <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
                                <Card className="border-0 shadow-sm rounded-4 mb-4">
                                    <CardBody className="p-4 p-md-5">
                                        <h4 className="fw-semibold mb-4">Job Description</h4>
                                        <p className="text-muted">{job.description}</p>
                                    </CardBody>
                                </Card>
                            </motion.section>

                            <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                                <Card className="border-0 shadow-sm rounded-4 mb-4">
                                    <CardBody className="p-4 p-md-5">
                                        <h4 className="fw-semibold mb-4">Responsibilities</h4>
                                        <ul className="list-unstyled">
                                            {job.responsibilities.map((resp, idx) => (
                                                <li key={idx} className="d-flex align-items-start gap-3 mb-3 text-muted">
                                                    <FiCheckCircle className="text-primary mt-1 flex-shrink-0" size={18} />
                                                    {resp}
                                                </li>
                                            ))}
                                        </ul>
                                    </CardBody>
                                </Card>
                            </motion.section>

                            <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
                                <Card className="border-0 shadow-sm rounded-4">
                                    <CardBody className="p-4 p-md-5">
                                        <h4 className="fw-semibold mb-4">Required Skills</h4>
                                        <div className="d-flex flex-wrap gap-2">
                                            {job.skills.map((skill) => (
                                                <span key={skill} className="px-3 py-2 rounded-pill skill-badge">
                                                    {skill}
                                                </span>
                                            ))}
                                        </div>
                                    </CardBody>
                                </Card>
                            </motion.section>
                        </Col>

                        {/* Sidebar - unchanged */}
                        <Col lg={4} className="mt-5 mt-lg-0">
                            <Card className="border-0 shadow-sm rounded-4 sticky-top" style={{ top: "100px" }}>
                                <CardBody className="p-4 p-md-5">
                                    <h5 className="fw-semibold mb-4">Job Summary</h5>
                                    <div className="d-flex flex-column gap-4">
                                        {/* ... your existing sidebar content ... */}
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="rounded bg-light d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
                                                <LuBuilding2 size={20} className="text-muted" />
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Department</small>
                                                <div className="fw-medium">{job.department}</div>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-3">
                                            <div className="rounded bg-light d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
                                                <FiMapPin size={20} className="text-muted" />
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Location</small>
                                                <div className="fw-medium">{job.location}</div>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-3">
                                            <div className="rounded bg-light d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
                                                <FiBriefcase size={20} className="text-muted" />
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Type</small>
                                                <div className="fw-medium">{job.type}</div>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-3">
                                            <div className="rounded bg-light d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
                                                <FiClock size={20} className="text-muted" />
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Experience</small>
                                                <div className="fw-medium">{job.experience}</div>
                                            </div>
                                        </div>

                                        {job.salary && (
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="rounded bg-light d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
                                                    <FiDollarSign size={20} className="text-muted" />
                                                </div>
                                                <div>
                                                    <small className="text-muted d-block">Salary</small>
                                                    <div className="fw-medium">{job.salary}</div>
                                                </div>
                                            </div>
                                        )}

                                        <Button
                                            color="primary"
                                            size="lg"
                                            className="w-100 mt-4 fw-semibold apply-btn"
                                            onClick={() => setApplyModal(true)}
                                        >
                                            Apply Now
                                        </Button>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>

                {/* Apply Modal - unchanged UI, updated form fields & submission */}
                <Modal
                    isOpen={applyModal}
                    toggle={() => !submitting && setApplyModal(false)}
                    size="lg"
                    centered
                    scrollable
                    className="job-apply-modal"
                >
                    <ModalHeader
                        toggle={() => !submitting && setApplyModal(false)}
                        className="job-apply-modal-header border-0"
                    >
                        <div className="d-flex align-items-center gap-3">
                            <div className="job-apply-icon-wrapper">
                                <FiBriefcase size={28} className="job-apply-icon" />
                            </div>
                            <div>
                                <h5 className="job-apply-modal-title mb-0">Apply for {job.title}</h5>
                                <small className="text-white">Please fill in the details below</small>
                            </div>
                        </div>
                    </ModalHeader>

                    <ModalBody className="job-apply-modal-body px-4 px-md-5 pb-5">
                        {submitted ? (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.92 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="text-center py-5 my-4"
                            >
                                <div className="job-apply-success-circle mx-auto mb-4">
                                    <FiCheckCircle size={64} className="text-success" />
                                </div>
                                <h4 className="job-apply-success-title fw-bold mb-3">
                                    Application Submitted!
                                </h4>
                                <p className="job-apply-success-text text-muted mb-4 px-3">
                                    Thank you for applying! We’ve received your application and will review it soon.
                                </p>
                                <Button
                                    color="primary"
                                    className="job-apply-browse-btn px-5 py-3"
                                    onClick={() => navigate("/careers")}
                                >
                                    Browse More Jobs
                                </Button>
                            </motion.div>
                        ) : (
                            <form onSubmit={handleSubmit} className="job-apply-form">
                                <Row className="g-4">
                                    <Col md={6}>
                                        <FormGroup className="job-apply-form-group">
                                            <Label className="job-apply-label">
                                                Full Name <span className="text-danger">*</span>
                                            </Label>
                                            <Input
                                                value={form.fullName}
                                                onChange={(e) => handleChange("fullName", e.target.value)}
                                                invalid={!!errors.fullName}
                                                className="job-apply-input"
                                                placeholder="Enter your full name"
                                            />
                                            <FormFeedback className="job-apply-feedback">
                                                {errors.fullName}
                                            </FormFeedback>
                                        </FormGroup>
                                    </Col>

                                    <Col md={6}>
                                        <FormGroup className="job-apply-form-group">
                                            <Label className="job-apply-label">
                                                Email Address <span className="text-danger">*</span>
                                            </Label>
                                            <Input
                                                type="email"
                                                value={form.email}
                                                onChange={(e) => handleChange("email", e.target.value)}
                                                invalid={!!errors.email}
                                                className="job-apply-input"
                                                placeholder="Enter your email address"
                                            />
                                            <FormFeedback className="job-apply-feedback">
                                                {errors.email}
                                            </FormFeedback>
                                        </FormGroup>
                                    </Col>

                                    <Col md={6}>
                                        <FormGroup className="job-apply-form-group">
                                            <Label className="job-apply-label">
                                                Phone Number <span className="text-danger">*</span>
                                            </Label>
                                            <Input
                                                value={form.phone}
                                                // onChange={(e) => handleChange("phone", e.target.value)}
                                                onChange={(e) => {
                                                    // allow only digits
                                                    const onlyNums = e.target.value.replace(/\D/g, "");
                                                    handleChange("phone", onlyNums);
                                                }}
                                                invalid={!!errors.phone}
                                                className="job-apply-input"
                                                placeholder="e.g. XXXXX XXXXX"
                                                maxLength={10}

                                            />
                                            <FormFeedback className="job-apply-feedback">
                                                {errors.phone}
                                            </FormFeedback>
                                        </FormGroup>
                                    </Col>

                                    <Col md={6}>
                                        <FormGroup className="job-apply-form-group">
                                            <Label className="job-apply-label">
                                                Current Location <span className="text-danger">*</span>
                                            </Label>
                                            <Input
                                                value={form.currentLocation}
                                                onChange={(e) => handleChange("currentLocation", e.target.value)}
                                                invalid={!!errors.currentLocation}
                                                className="job-apply-input"
                                                placeholder="Pune, Maharashtra"
                                            />
                                            <FormFeedback className="job-apply-feedback">
                                                {errors.currentLocation}
                                            </FormFeedback>
                                        </FormGroup>
                                    </Col>

                                    <Col md={6}>
                                        <FormGroup className="job-apply-form-group">
                                            <Label className="job-apply-label">
                                                Total Experience <span className="text-danger">*</span>
                                            </Label>
                                            <Input
                                                value={form.totalExperience}
                                                onChange={(e) => handleChange("totalExperience", e.target.value)}
                                                invalid={!!errors.totalExperience}
                                                className="job-apply-input"
                                                placeholder="e.g. 4 years"
                                            />
                                            <FormFeedback className="job-apply-feedback">
                                                {errors.totalExperience}
                                            </FormFeedback>
                                        </FormGroup>
                                    </Col>

                                    <Col md={6}>
                                        <FormGroup className="job-apply-form-group">
                                            <Label className="job-apply-label">
                                                Key Skills <span className="text-danger">*</span>
                                            </Label>
                                            <Input
                                                value={form.skills}
                                                onChange={(e) => handleChange("skills", e.target.value)}
                                                invalid={!!errors.skills}
                                                className="job-apply-input"
                                                placeholder=" e.g. HTML, CSS, React, JavaScript, etc."
                                            />
                                            <FormFeedback className="job-apply-feedback">
                                                {errors.skills}
                                            </FormFeedback>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                {/* Resume Upload */}
                                <FormGroup className="job-apply-form-group mt-4">
                                    <Label className="job-apply-label">
                                        Upload Resume <span className="text-danger">*</span>
                                    </Label>
                                    <div className={`job-apply-upload-zone ${errors.resume ? "error" : form.resume ? "success" : ""}`}>
                                        <input
                                            type="file"
                                            accept=".pdf,.doc,.docx"
                                            onChange={handleFileChange}
                                            className="job-apply-file-input"
                                        />
                                        {form.resume ? (
                                            <div className="job-apply-file-preview">
                                                <div className="d-flex align-items-center gap-3">
                                                    <FiCheckCircle size={28} className="text-success" />
                                                    <div>
                                                        <div className="fw-medium">{form.resume.name}</div>
                                                        <small className="text-muted">
                                                            {(form.resume.size / 1024 / 1024).toFixed(2)} MB
                                                        </small>
                                                    </div>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    color="link"
                                                    className="text-danger p-0"
                                                    onClick={() => setForm((p) => ({ ...p, resume: null }))}
                                                >
                                                    Remove
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="job-apply-upload-placeholder">
                                                <FiUpload size={40} className="text-primary mb-3" />
                                                <p className="mb-1 fw-medium">Click or drag resume here</p>
                                                <small className="text-muted">
                                                    PDF, DOC, DOCX • Maximum 5MB
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                    {errors.resume && (
                                        <FormFeedback className="job-apply-feedback d-block mt-2">
                                            {errors.resume}
                                        </FormFeedback>
                                    )}
                                </FormGroup>

                                <FormGroup className="job-apply-form-group mt-4">
                                    <Label className="job-apply-label">Cover Letter (Optional)</Label>
                                    <Input
                                        type="textarea"
                                        rows="5"
                                        value={form.coverLetter}
                                        onChange={(e) => handleChange("coverLetter", e.target.value)}
                                        className="job-apply-textarea"
                                        placeholder="Tell us why you're a great fit for this role..."
                                    />
                                </FormGroup>

                                <div className="job-apply-actions d-flex justify-content-end gap-3 mt-5 pt-3 border-top">
                                    <Button
                                        outline
                                        color="secondary"
                                        className="job-apply-cancel-btn px-5 py-3"
                                        onClick={() => setApplyModal(false)}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        color="primary"
                                        type="submit"
                                        className="job-apply-submit-btn px-5 py-3"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <FiLoader className="me-2 animate-spin" size={18} />
                                                Submitting...
                                            </>
                                        ) : (
                                            "Submit Application"
                                        )}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </ModalBody>
                </Modal>
            </div>
        </div>
    );
};

export default JobDetails;